import type { Seo } from '@sravni/types/lib/seo';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { TFindParams } from '../../server/services/metadata';
import { loadMetadata } from '../../services/metadata';
import type { GlobalState } from '../index';
import { getMetadata } from '../selectors/metadata';

export type IState = {
  data: Record<string, Seo>;
  isLoading: boolean;
};

const initialState: IState = {
  data: {},
  isLoading: false,
};

export const fetchMetadata = createAsyncThunk<
  {
    pathname: string;
    seo: Seo;
  },
  TFindParams,
  {
    state: GlobalState;
  }
>(`metadata/fetchMetadata`, async (params, { getState }) => {
  const state = getState();
  let seo = getMetadata(params.url)(state);

  if (!seo) {
    seo = await loadMetadata(params);
  }

  return {
    pathname: params.url,
    seo,
  };
});

const metadataSlice = createSlice({
  name: 'metadata',
  initialState,
  reducers: {
    setMetadata: (
      state,
      {
        payload,
      }: PayloadAction<{
        pathname: string;
        seo: Seo;
      }>,
    ) => {
      state.data[payload.pathname] = payload.seo;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMetadata.fulfilled, (state, { payload }) => {
      state.data[payload.pathname] = payload.seo;
      state.isLoading = false;
    });
    builder.addCase(fetchMetadata.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchMetadata.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

const { actions, reducer } = metadataSlice;

export const { setMetadata } = actions;

export default reducer;
